(ns practitest.frontend.components.comments3
  (:require [ajax.core :as ajax]
            [clojure.string :as string]
            [practitest.frontend.common-entities.metadata :as metadata]
            [practitest.frontend.common.bugtrackers :as bugtrackers]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.format-utils :as fu]
            [practitest.frontend.common.i18n :refer [format-translation
                                                     get-translation]]
            [practitest.frontend.common.permissions :as permissions]
            [practitest.frontend.common.re-frame :refer [conditional-dispatch
                                                         partial-dispatch
                                                         partial-subscribe]]
            [practitest.frontend.components.confirmation :as confirmation]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.markdown :as markdown]
            [practitest.frontend.components.navigation :refer [user-avatar2]]
            [practitest.frontend.components.notifications :as notifications]
            [practitest.frontend.components.tooltip :as tooltip]
            [practitest.frontend.components.users :as users]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.events :as events]
            [practitest.frontend.forms3.entity-form.utils :refer [track!]]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.subs :as subs]
            [re-frame.core :as re-frame]
            [reagent.core :as reagent]))

;;; subscriptions
(re-frame/reg-sub
  ::comments
  (fn [[_ entity]]
    (re-frame/subscribe [::subs/entity entity]))
  (fn [{:keys [comments]} _]
    (:data comments)))

(re-frame/reg-sub
  ::can-add-comments?
  (fn [[_ entity-type]]
    (re-frame/subscribe [::permissions/has-permission? (permissions/comment-for-entity entity-type)]))
  (fn [has-permission?]
    (boolean has-permission?)))

(re-frame/reg-sub
  ::can-edit-comment?
  (fn [[_ entity-type _]]
    [(re-frame/subscribe [::can-add-comments? entity-type])
     (re-frame/subscribe [::subs/current-user])])
  (fn [[can-add-comments? current-user] [_ _ comment]]
   ;; TODO: shouldn't we also allow editing of any comments by project admin or account owner?
    (and can-add-comments?
        ;; Do not allow editing comments from external systems
         (not (:external_system comment))
         (= (:id current-user) (:user_id comment)))))

(re-frame/reg-sub
 ::can-delete-comment?
 (fn [[_ entity-type _]]
   [(re-frame/subscribe [::can-add-comments? entity-type])
    (re-frame/subscribe [::subs/current-user])
    (re-frame/subscribe [::subs/current-project])])
 (fn [[can-add-comments? current-user current-project] [_ _ comment]]
   (and can-add-comments?
        (not (:external_system comment))
        (or
         (= (:id current-user) (:user_id comment))
         (permissions/project-administrator? current-project)))))

;;; events
(defn add-comment [{db :db}
                   [_ body {:keys [entity-id entity-type] :as add-options}]]
  (let [table-name (get-in metadata/entities-metadata [entity-type :db-table-name])
        progress-event [::add-comment entity-type entity-id]]
    {:db (update db :progress conj progress-event)
     :fx [[:http-xhrio {:method          :post
                        :uri             (broutes/get-url "comments#create"
                                                          {:project_id (subs/get-current-project-id db)})
                        :params          {:comment        body
                                          :table_name     table-name
                                          :commentable_id entity-id}
                        :format          (ajax/json-request-format)
                        :response-format (ajax/json-response-format {:keywords? true})
                        :on-success      [::added-comment add-options]
                        :on-failure      [::events/api-error progress-event {:message "Error adding comment"}]}]]}))

(re-frame/reg-event-fx
  ::add-comment
  add-comment)

(defn- handle-comment-operation [notification-message]
  {:dispatch-n [[::events/fetch-comments]
                [::events/fetch-history]
                [::notifications/success2 notification-message {:auto-close 1000}]]})

(re-frame/reg-event-fx
  ::added-comment
  (fn [{db :db} [_ {:keys [entity-id entity-type on-success-callback added-comment-event] :as add-options} response]]
    (track! "Add Comment" entity-type)
    (when on-success-callback (on-success-callback))
    (-> (handle-comment-operation (get-translation "comments.notifications.added" "Comment added"))
        (assoc :db (update db :progress disj [::add-comment entity-type entity-id]))
        (conditional-dispatch added-comment-event add-options response))))

(defn save-comment [{db :db}
                    [_ id body {:keys [entity-type] :as save-options}]]
  (let [uri (broutes/get-url "comments#update" {:project_id (subs/get-current-project-id db)
                                                :id         id})]
    {:db (update db :progress conj [::save-comment id])
     :fx [[:http-xhrio {:method :put
                        :uri uri
                        :params {:body body}
                        :format (ajax/json-request-format)
                        :response-format (ajax/json-response-format {:keywords? true})
                        :on-success [::saved-comment id save-options]
                        :on-failure [::events/api-error [::save-comment id] {:message "Error saving comment"}]}]]}))

(re-frame/reg-event-fx
  ::save-comment
  save-comment)

(re-frame/reg-event-fx
  ::saved-comment
  (fn [{db :db} [_ id {:keys [entity-type updated-comment-event] :as save-options} response]]
    (track! "Update Comment" entity-type)
    (-> (handle-comment-operation (get-translation "comments.notifications.saved" "Comment saved"))
        (assoc :db (update db :progress disj [::save-comment id]))
        (conditional-dispatch updated-comment-event save-options response))))

(defn delete-comment [{db :db}
                      [_ id {:keys [entity-type] :as delete-options}]]
  (track! "Delete Comment" entity-type)
  (let [uri (broutes/get-url "comments#destroy" {:project_id (subs/get-current-project-id db)
                                                 :id         id})]
    {:db (update db :progress conj [::delete-comment id])
     :fx [[:http-xhrio {:method :delete
                        :uri uri
                        :format (ajax/url-request-format)
                        :response-format (ajax/json-response-format {:keywords? true})
                        :on-success [::deleted-comment id delete-options]
                        :on-failure [::events/api-error [::delete-comment id] {:message "Error deleting comment"}]}]]}))

(re-frame/reg-event-fx
  ::delete-comment
  delete-comment)

(re-frame/reg-event-fx
  ::deleted-comment
  (fn [{db :db} [_ id {:keys [deleted-comment-event] :as delete-options} response]]
    (-> (handle-comment-operation (get-translation "comments.notifications.deleted" "Comment deleted"))
        (assoc :db (update db :progress disj [::delete-comment id]))
        (conditional-dispatch deleted-comment-event delete-options response))))

;;; components

(defn confirm-delete [{:keys [show? hide! on-delete]}]
  [confirmation/confirm
   {:show? show?
    :title (get-translation "comments.delete-confirmation.title" "Delete Comment?")
    :body (get-translation "comments.delete-confirmation.body"
                           "The comment will be deleted permanently")
    :yes-label (get-translation "generic.button.delete" "Delete")
    :on-yes (fn [_]
              (hide!)
              (on-delete))
    :no-label (get-translation "generic.button.cancel" "Cancel")
    :on-no hide!}])

(defn comment-editor [{:keys [users value on-change id auto-focus]}]
  [markdown/tribute-textarea
   {:on-change #(on-change (dom/event-value %))
    :on-tribute-replaced #(on-change (dom/event-value %))
    :max-length 5000
    :id id
    :value @value
    :aria-label (get-translation "entity.comment.placeholder"
                                 "Add a comment...")
    :placeholder (get-translation "entity.comment.placeholder"
                                  "Add a comment...")
    :auto-focus auto-focus
    :users (filter #(= "user" (:type %)) @users)}])

(defn- new-comment [{:keys [main-state entity-comment? new?]}]
  (let [state (if entity-comment? main-state (reagent/atom {:body ""}))]
    (fn [{:keys [entity-type entity-id add-comment added-comment-event] :as props}]
      [:div.comment-new-container
       [:div.comment-editor-container
        [comment-editor
         (merge props
                {:on-change (fn [new-value]
                              (swap! state assoc :new-comment new-value))
                 :value (reagent/cursor state [:new-comment])
                 :id "new-comment"})]
        [:div.add-comment-button
         [buttons/button
          {:class "add-comment-button"
           :disabled? (or new? (string/blank? (:new-comment @state)))
           :on-click (fn [_]
                       (add-comment (:new-comment @state)
                                    {:entity-type entity-type
                                     :entity-id entity-id
                                     :added-comment-event added-comment-event
                                     :on-success-callback #(swap! state assoc :new-comment "")}))
           :icon [icons/paper-plane]}]]]])))

(defn edited-text [comment]
  [tooltip/mux-tooltip
   {}
   [:span
    (format-translation
      (get-translation "comments.info.edited-at"
                       "Edited at %{date-time}")
      :date-time (fu/formatted-date-time (:updated_at comment)))]
   (fn [props]
     [:span.edited-text props
      " (" (get-translation "comments.info.edited" "edited") ")"])])

(defn- edit-mode [comment {:keys [entity-type entity-id updated-comment-event] :as props} state]
  (let [comment-state (reagent/atom (:body comment))
        {:keys [update-comment]} props]
    [:div.comment-editor-container
     [comment-editor
      (merge props
             {:on-change #(reset! comment-state %)
              :value comment-state
              :auto-focus true
              :id (str "comment-" (:id comment))})]

     [:div.action-buttons
      [buttons/button
       {:on-click (fn [_]
                    (reset! comment-state (:body comment))
                    (swap! state assoc :editing? false))
        :text (get-translation "generic.button.cancel" "Cancel")
        :size "small"
        :primary? false}]
      [buttons/button
       {:on-click (fn [_]
                    (update-comment (:id comment) @comment-state {:entity-type entity-type
                                                                  :entity-id entity-id
                                                                  :updated-comment-event updated-comment-event})
                    (swap! state assoc :editing? false))
        :text (get-translation "generic.button.update" "Update")
        :size "small"
        :primary? true}]]]))

(defn- view-mode [comment user {:keys [can-edit-comment? can-delete-comment? delete-comment entity-type
                                       entity-id deleted-comment-event snapshot?]} state]
  (let [show-confirm-delete? (reagent/cursor state [:show-delete-confirmation?])
        show-confirm-delete! #(swap! state assoc :show-delete-confirmation? true)
        hide-confirm-delete! #(swap! state assoc :show-delete-confirmation? false)]
    [:div.comment-container
     [:div.comment-header
      [:div
       [:span.comment-author-name
               ;; Use external user if present first
        (:user_name comment)]
       [:span.comment-created
        (format-translation
          (get-translation "comments.info.commented-at"
                           "Commented at %{date-time}")
          :date-time (fu/formatted-date-time (:created_at comment)))
        (when (not= (:created_at comment)
                    (:updated_at comment))
          [edited-text comment])]]

      (when-let [system (:external_system comment)]
        (let [logo (bugtrackers/get-logo-path system)
              label (bugtrackers/get-label system)]
          [:div.external-system-logo
           [:img {:src logo :alt label :title label}]
           label]))]

     [:div.comment-body
      (let [parts (partition-all 2 (string/split (:body comment) #"\[~(.*?)\]" -1))]
        (into [:<>]
              (mapcat
                (fn [[text email]]
                  [text
                   (when email
                     [:span.mention-text email])])
                parts)))

      (when-not snapshot?
        [:div.action-buttons
           ;; TODO: button component ?
         (when @(can-edit-comment? comment)
           ;; TODO: button component ?
           [:button {:on-click #(swap! state assoc :editing? true)}
            [icons/mux-pencil]])
         (when @(can-delete-comment? comment)
           [:button {:on-click show-confirm-delete!}
            [icons/mux-trash]])])]

     [confirm-delete
      {:show? show-confirm-delete?
       :hide! hide-confirm-delete!
       :on-delete #(delete-comment (:id comment) {:entity-type entity-type
                                                  :entity-id entity-id
                                                  :deleted-comment-event deleted-comment-event})}]]))

(defn comment-view [{:keys [users] :as props} _]
  (let [state (reagent/atom {:editing? false :show-delete-confirmation? false})]
    (fn [_ comment]
      (let [user (users/find-user @users (:user_id comment))]
        [:div.comment-card
         [:div.comment-author
          ;; Do not show avatar for external users
          (when (nil? (:external_user_name comment))
            [user-avatar2 user])]
         (if (:editing? @state)
           [edit-mode comment props state]
           [view-mode comment user props state])]))))

;; Default props values for entity form wiring
(defn- prepare-props [entity-type props]
  (merge
    {:comments (re-frame/subscribe [::comments entity-type])
     :users (re-frame/subscribe [::subs/users])
     :main-state (:main-state props)
     :can-add-comments? (re-frame/subscribe [::can-add-comments? entity-type])
     :can-edit-comment? (partial-subscribe [::can-edit-comment? entity-type])
     :can-delete-comment? (partial-subscribe [::can-delete-comment? entity-type])
     :add-comment (partial-dispatch [::add-comment])
     :update-comment (partial-dispatch [::save-comment]) 
     :delete-comment (partial-dispatch [::delete-comment])}
    props))

(defn comments-view [{:keys [entity-type entity-id new?] :as props}]
  (let [{:keys [can-add-comments? comments snapshot?] :as props} (prepare-props entity-type props)]
    [:div.comments3-container
     (when (and @can-add-comments? (not snapshot?))
       [new-comment props])
     (into [:<>]
           (for [comment @comments]
             ^{:key (str "comment-" (:id comment))}
             [comment-view props comment]))]))

